import type {AsyncData, AsyncDataOptions} from "nuxt/app";

import type {Ref} from "vue";
import {toRef, unref, watch} from "vue";
import {useAsyncData} from "nuxt/app";

export type AsyncDataOptionsWithPausing<T> = AsyncDataOptions<T> & {
	pause?: Ref<boolean> | boolean;
};

export function useAsyncDataWithPausing<T, E = unknown>(
	key: string,
	handler: () => Promise<T>,
	options: AsyncDataOptionsWithPausing<T> = {},
): AsyncData<T, E> {
	const {pause, ...asyncOptions} = options;

	const pauseRef = toRef(pause ?? false);

	// If not paused initially, fetch data immediately
	if (!unref(pauseRef)) {
		// Casting is needed for some reason here because we're using the generic T parameter
		return useAsyncData(key, handler, asyncOptions) as AsyncData<T, E>;
	}

	// If paused initially, don't fetch right away - wait for paused to become false
	const asyncRes = useAsyncData(key, handler, {
		immediate: false,
		...asyncOptions,
	});

	// Watch for paused to become false, then execute
	const unwatch = watch(pauseRef, (isPaused) => {
		if (isPaused) {
			return;
		}

		asyncRes.execute();
		unwatch();
	});

	// Casting is needed for some reason here because we're using the generic T parameter
	return asyncRes as AsyncData<T, E>;
}
